import { useSnapshot } from 'valtio'
import fileOpenImage from '../../assets/errors/file-open.png'
import networkImage from '../../assets/errors/network.png'
import versionImage from '../../assets/errors/version-unsupported.png'
import playbackImage from '../../assets/errors/empty-replay.png'
import renderImage from '../../assets/errors/render.png'
import unknownImage from '../../assets/errors/unknown.png'
import { RepeatIcon } from '../settings/icons'
import ErrorScreen, { ErrorScreenImageProps, ErrorScreenProps } from './ErrorScreen'
import { ErrorScreenKind, errorScreenState } from './errorScreenState'

/** Artwork placement inside the 452x191 stage, measured from the mockup. */
const IMAGES: Record<ErrorScreenKind, ErrorScreenImageProps> = {
  'replay-open': { src: fileOpenImage, left: 151.5, top: 7, width: 169, height: 178 },
  network: { src: networkImage, left: 0.2, top: 59.7, width: 451.5, height: 77.2 },
  'version-unsupported': { src: versionImage, left: 145.1, top: 10.7, width: 158, height: 177.7 },
  playback: { src: playbackImage, left: 123.1, top: 25.5, width: 210, height: 143.5 },
  render: { src: renderImage, left: 83.4, top: 15.5, width: 267.9, height: 185.4 },
  'not-found': { src: unknownImage, left: 139.7, top: 4, width: 185.6, height: 175.7 },
  unknown: { src: unknownImage, left: 139.7, top: 4, width: 185.6, height: 175.7 }
}

const restart = () => {
  window.location.reload()
}

const RETRY = { label: 'Повторить', onClick: restart, icon: <RepeatIcon size={16} /> } as const
const RESTART = {
  label: 'Начать сначала', onClick: restart, icon: <RepeatIcon size={16} />, variant: 'primary'
} as const

const SCREENS: Record<ErrorScreenKind, Omit<ErrorScreenProps, 'image' | 'code'>> = {
  'replay-open': {
    title: 'Не удалось открыть запись',
    description: 'Файл записи недоступен или повреждён. Обновите страницу или попробуйте снова через некоторое время.',
    actions: [RETRY]
  },
  network: {
    title: 'Ошибка подключения',
    description: 'Проверьте подключение к интернету. Если включён VPN, отключите его и попробуйте снова.',
    actions: [RETRY]
  },
  'version-unsupported': {
    title: 'Версия игры пока не поддерживается',
    description: 'Запись сделана на Minecraft {version}, а плеер умеет открывать записи до версии {maxVersion}. Поддержку новых версий уже в процессе добавления.'
  },
  playback: {
    title: 'Не получилось воспроизвести запись',
    description: 'Что-то пошло не так во время проигрывания. Возможно, запись содержит данные, которые плеер пока не умеет обрабатывать.',
    actions: [RESTART]
  },
  render: {
    title: 'Ошибка рендеринга',
    description: 'У вашего браузера не получилось отрисовать мир. Попробуйте обновить страницу или открыть запись в Chrome либо Firefox последних версий.'
  },
  'not-found': {
    title: 'Страница не найдена',
    description: 'Записи открываются по ссылке. Проверьте адрес или загрузите файл записи вручную.'
  },
  unknown: {
    title: 'Что-то сломалось',
    description: 'Произошла неожиданная ошибка. Обновите страницу — обычно помогает. Если ошибка повторится, пришлите её код в поддержку.',
    actions: [RESTART]
  }
}

export const buildErrorScreen = (
  kind: ErrorScreenKind,
  params: Record<string, string> = {},
  code = ''
): ErrorScreenProps => {
  const screen = SCREENS[kind]
  return {
    ...screen,
    image: IMAGES[kind],
    code: code || undefined,
    description: screen.description.replaceAll(
      /{(\w+)}/g,
      (whole, key: string) => params[key] ?? whole
    )
  }
}

export default () => {
  const { kind, params, code } = useSnapshot(errorScreenState)
  if (!kind) return null
  return <ErrorScreen {...buildErrorScreen(kind, { ...params }, code)} />
}
